import React, { useState } from "react";
import { createPortal } from "react-dom";
import type { ClassDetailResponse, ReservedStudent } from "../../../../../interfaces/classDetail";
import { useLocalization } from "../../../../../stores/localizationContext";
import { useHttpClient } from "../../../../../hooks/useHttpClient";
import { useUserContext } from "../../../../../stores/userContext";
import AdminTable, { type AdminTableColumn } from "../../../Common/AdminTable/AdminTable";
import AddStudentPopup from "../AddStudentPopup/AddStudentPopup";
import "./StudentsList.scss";

interface StudentsListProps {
  data: ClassDetailResponse;
  classId: string;
  onRefresh: () => void;
}

const formatBlocks = (blocks: number[] | null): string => {
  if (!blocks || blocks.length === 0) return "—";
  return `Block ${blocks.join(", ")}`;
};

const getNextBlock = (student: ReservedStudent, noOfBlocks: number): number | null => {
  const blocks = student.access_rights.blocks_numbers;
  if (!blocks || blocks.length === 0) return noOfBlocks > 0 ? 1 : null;
  const maxBlock = Math.max(...blocks);
  const next = maxBlock + 1;
  return next <= noOfBlocks ? next : null;
};

const StudentsList: React.FC<StudentsListProps> = ({ data, classId, onRefresh }) => {
  const { t } = useLocalization();
  const { user } = useUserContext();
  const api = useHttpClient();
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [activatingKey, setActivatingKey] = useState<string | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [pendingActivation, setPendingActivation] = useState<{ student: ReservedStudent; blockNumber: number } | null>(null);

  const handleActivateBlock = async (student: ReservedStudent, blockNumber: number) => {
    if (!user?.userId) return;
    const key = `${student.student_id}_${blockNumber}`;
    setActivatingKey(key);
    setActivateError(null);
    setPendingActivation(null);
    try {
      const endpoint = import.meta.env.VITE_ADD_STUDENT_AFFILIATION
        .replace("{educator_id}", user.userId)
        .replace("{class_id}", classId);
      await api.post(endpoint, {
        student_id: student.student_id,
        blocks: [blockNumber],
      });
      onRefresh();
    } catch (err: any) {
      const msg = err?.response?.data?.error_message;
      setActivateError(msg ?? t("generic_error", "Something went wrong. Please try again later."));
    } finally {
      setActivatingKey(null);
    }
  };

  const columns: AdminTableColumn<ReservedStudent>[] = [
    {
      key: "name",
      header: t("student_name_label", "Student Name"),
      cellDir: "auto",
      render: (student) => `${student.student_first_name} ${student.student_last_name}`,
    },
    {
      key: "type",
      header: t("student_type_label"),
      render: (student) =>
        student.is_affiliate
          ? t("student_type_affiliate_label")
          : t("nagwa_name_label"),
    },
    {
      key: "mobile",
      header: t("mobile_number_label"),
      cellDir: "ltr",
      render: (student) => student.student_mobile_number ?? "—",
    },
    {
      key: "blocks",
      header: t("paid_blocks_label"),
      render: (student) => formatBlocks(student.access_rights.blocks_numbers),
    },
    {
      key: "activation",
      header: t("activation_label"),
      render: (student) => {
        const nextBlock = getNextBlock(student, data.no_of_blocks);
        if (nextBlock === null || !student.is_affiliate) return null;

        return (
          <button
            className="students-list__activate-btn"
            onClick={() => setPendingActivation({ student, blockNumber: nextBlock })}
            disabled={activatingKey === `${student.student_id}_${nextBlock}`}
          >
            {activatingKey === `${student.student_id}_${nextBlock}`
              ? t("activating", "...")
              : `${t("activate_block", "Activate Block")} ${nextBlock}`}
          </button>
        );
      },
    },
  ];

  return (
    <div className="students-list">
      <div className="students-list__header">
        <h2 className="students-list__title">{t("students_list_label")}</h2>
        <button
          className="students-list__add-btn"
          onClick={() => setShowAddPopup(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512" className="students-list__add-icon">
            <path d="M96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM0 482.3C0 383.8 79.8 304 178.3 304l91.4 0C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7L29.7 512C13.3 512 0 498.7 0 482.3zM472 200l0-40c0-13.3 10.7-24 24-24s24 10.7 24 24l0 40 40 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-40 0 0 40c0 13.3-10.7 24-24 24s-24-10.7-24-24l0-40-40 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l40 0z"/>
          </svg>
          {t("add_affiliate_student_button")}
        </button>
      </div>

      <AdminTable
        containerClassName="students-list__table"
        data={data.reserved_students}
        columns={columns}
        rowKey={(student) => student.student_id}
        emptyState={t("no_students", "No students enrolled yet.")}
      />

      {activateError && (
        <div className="students-list__activate-error">
          {activateError}
        </div>
      )}

      {showAddPopup && (
        <AddStudentPopup
          classId={classId}
          onCancel={() => setShowAddPopup(false)}
          onStudentAdded={() => {
            setShowAddPopup(false);
            onRefresh();
          }}
        />
      )}

      {pendingActivation && createPortal(
        <div className="modal-overlay" onClick={() => setPendingActivation(null)}>
          <div
            className="activate-confirm-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="activate-confirm-popup__title">
              {t("activate_student_title")}
            </h3>
            <p className="activate-confirm-popup__message">
              {t(
                "activate_student_confirm",
                `You are going to activate ${pendingActivation.student.student_first_name} ${pendingActivation.student.student_last_name} in block ${pendingActivation.blockNumber}, Are you sure to continue?`
              )}
            </p>
            <button
              className="activate-confirm-popup__yes-btn"
              onClick={() => handleActivateBlock(pendingActivation.student, pendingActivation.blockNumber)}
              disabled={activatingKey === `${pendingActivation.student.student_id}_${pendingActivation.blockNumber}`}
            >
              {activatingKey === `${pendingActivation.student.student_id}_${pendingActivation.blockNumber}`
                ? t("activating", "...")
                : t("yes_label")}
            </button>
            <button
              className="activate-confirm-popup__no-btn"
              onClick={() => setPendingActivation(null)}
            >
              {t("no_label")}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StudentsList;
